import { useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { sessionState } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

import { targetSelectState } from '@/state/project';

const PLACEHOLDER = '—';

export default function TargetSelectDialog() {
  const [payload, setPayload] = useRecoilState(targetSelectState);
  const session = useRecoilValue(sessionState);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const hosts = payload?.hosts ?? [];
  // Each scanned target gets its own pinned Kali terminal (+ one recon terminal),
  // so the sweep scans at most this many at once. UI limiter mirrors the backend cap.
  const MAX_SELECT = 4;
  const atCap = selected.size >= MAX_SELECT;
  const selectableCount = Math.min(hosts.length, MAX_SELECT);
  const allSelected = selectableCount > 0 && selected.size === selectableCount;
  const someSelected = selected.size > 0 && !allSelected;

  const orderedSelection = useMemo(
    () => hosts.map((h) => h.id).filter((id) => selected.has(id)),
    [hosts, selected]
  );

  if (!payload) return null;

  const emit = (action: 'confirm' | 'none' | 'cancel', ids: string[]) => {
    const socket = session?.socket;
    console.debug('[target-select] emit', {
      action,
      selected_ids: ids,
      form_id: payload.form_id
    });
    if (socket && typeof socket.emit === 'function') {
      socket.emit('target_selection_response', {
        form_id: payload.form_id,
        action,
        selected_ids: ids
      });
    }
    setSelected(new Set());
    setPayload(null);
  };

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        if (next.has(id) || next.size >= MAX_SELECT) return prev;
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelected(
      checked ? new Set(hosts.slice(0, MAX_SELECT).map((h) => h.id)) : new Set()
    );
  };

  const handleConfirm = () => {
    if (orderedSelection.length === 0) {
      emit('none', []);
      return;
    }
    emit('confirm', orderedSelection);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && emit('cancel', [])}>
      <DialogContent
        className="w-[92vw] max-w-[900px] max-h-[85vh] flex flex-col gap-4 p-6 overflow-hidden"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Discovered hosts</DialogTitle>
          <DialogDescription>
            Select up to {MAX_SELECT} hosts to begin reconnaissance on (each
            scans on its own terminal). Confirm with none selected to leave them
            pending.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto rounded-md border border-border">
          <div className="hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        allSelected
                          ? true
                          : someSelected
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={(checked) => toggleAll(checked === true)}
                      aria-label="Select all hosts"
                    />
                  </TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Hostname</TableHead>
                  <TableHead>OS</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>MAC</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hosts.map((host) => {
                  const checked = selected.has(host.id);
                  return (
                    <TableRow
                      key={host.id}
                      data-state={checked ? 'selected' : undefined}
                      className="cursor-pointer"
                      onClick={() => toggle(host.id, !checked)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={checked}
                          disabled={!checked && atCap}
                          onCheckedChange={(value) =>
                            toggle(host.id, value === true)
                          }
                          aria-label={`Select ${host.ip}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono">{host.ip}</TableCell>
                      <TableCell>{host.hostname || PLACEHOLDER}</TableCell>
                      <TableCell>{host.os || PLACEHOLDER}</TableCell>
                      <TableCell>{host.vendor || PLACEHOLDER}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {host.mac || PLACEHOLDER}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <ul className="divide-y divide-border sm:hidden">
            {hosts.map((host) => {
              const checked = selected.has(host.id);
              return (
                <li
                  key={host.id}
                  className="flex cursor-pointer items-start gap-3 p-3"
                  onClick={() => toggle(host.id, !checked)}
                >
                  <span className="mt-1" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={checked}
                      disabled={!checked && atCap}
                      onCheckedChange={(value) =>
                        toggle(host.id, value === true)
                      }
                      aria-label={`Select ${host.ip}`}
                    />
                  </span>
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="font-mono font-medium">{host.ip}</div>
                    <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
                      <dt className="text-muted-foreground">Hostname</dt>
                      <dd className="truncate">
                        {host.hostname || PLACEHOLDER}
                      </dd>
                      <dt className="text-muted-foreground">OS</dt>
                      <dd className="truncate">{host.os || PLACEHOLDER}</dd>
                      <dt className="text-muted-foreground">Vendor</dt>
                      <dd className="truncate">{host.vendor || PLACEHOLDER}</dd>
                      <dt className="text-muted-foreground">MAC</dt>
                      <dd className="truncate font-mono">
                        {host.mac || PLACEHOLDER}
                      </dd>
                    </dl>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {selected.size} of {MAX_SELECT} max selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => emit('cancel', [])}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>
              {orderedSelection.length === 0
                ? 'Skip (none)'
                : `Recon ${orderedSelection.length} host(s)`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
